import { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";

interface PageTransitionProps {
  children: React.ReactNode;
}

/**
 * PageTransition wrapper for smooth route transitions
 * - Uses fade + scale (transform/opacity only - GPU accelerated)
 * - Respects prefers-reduced-motion
 * - Uses location.pathname as stable key to prevent re-render loops
 * - Optimized with RAF for smooth animations
 */
export const PageTransition = ({ children }: PageTransitionProps) => {
  const location = useLocation();
  const [isAnimating, setIsAnimating] = useState(false);
  const prevPathRef = useRef(location.pathname);
  const rafRef = useRef<number>();

  useEffect(() => {
    // Only animate on actual route changes
    if (prevPathRef.current !== location.pathname) {
      setIsAnimating(true);
      prevPathRef.current = location.pathname;

      // Use RAF for smooth timing
      let timer: NodeJS.Timeout;
      rafRef.current = requestAnimationFrame(() => {
        timer = setTimeout(() => {
          setIsAnimating(false);
        }, 300); // Match CSS transition duration
      });

      return () => {
        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current);
        }
        if (timer) {
          clearTimeout(timer);
        }
      };
    }
  }, [location.pathname]);

  return (
    <div
      className={cn(
        "transition-opacity duration-300 motion-reduce:transition-none",
        isAnimating ? "opacity-0" : "opacity-100"
      )}
      style={{
        willChange: isAnimating ? "opacity" : "auto",
        minHeight: "50vh", // Prevent CLS
      }}
    >
      {children}
    </div>
  );
};
